import type { CrmObject } from '@/types/crm';

import type { Stage } from '../../hooks/useStages';
import { getStageEntityId } from '../../utils/propertyHelpers';

export type HighlightType = 'overdue' | 'new_attendee' | 'risk' | 'info';

export const VALID_HIGHLIGHT_TYPES: readonly HighlightType[] = [
  'overdue',
  'new_attendee',
  'risk',
  'info',
];

export interface Highlight {
  type: HighlightType;
  text: string;
}

export interface AISummary {
  preamble: string;
  highlights: Highlight[];
  focus_areas: string[];
}

export interface SectionMetadata {
  text?: string;
  badges?: Array<{ label: string; color?: string }>;
}

export interface FlatSectionMetadata {
  meeting_count?: number;
  email_count?: number;
  note_count?: number;
  new_attendee_count?: number;
  new_attendee_ids?: string[];
  has_active_deals?: boolean;
}

export interface ActionItem {
  text: string;
  /** Backend always populates when action items come from a meeting; optional for safety. */
  source_meeting_title?: string;
  /** Backend always populates when action items come from a meeting; optional for safety. */
  source_meeting_date?: string;
}

export interface MeetingPrepPerson {
  entity_id: string | null;
  name: string | null;
  title: string | null;
  company_name: string | null;
  email: string;
  is_new: boolean;
  research_entity_id: string | null;
}

export interface MeetingPrepCompanyProperties {
  industry: string | null;
  headcount: string | null;
  location: string | null;
  founded: string | null;
  [key: string]: string | null; // future-proof for additional properties
}

export interface MeetingPrepCompany {
  entity_id: string;
  name: string | null;
  description: string | null;
  properties: MeetingPrepCompanyProperties;
  research_entity_id: string | null;
}

export interface MeetingPrepViewDefinition {
  version: string;
  mode: 'first_call' | 'existing_relationship';
  ai_summary: AISummary | null;
  /**
   * Structured per-section metadata from the backend. When absent, the frontend
   * derives section metadata from the flat `section_metadata` field via
   * deriveSectionsFromFlatMetadata(). The current backend sends the flat form;
   * this field is reserved for future structured form.
   */
  sections?: Record<string, { section_metadata?: SectionMetadata }>;
  section_metadata?: FlatSectionMetadata; // flat form sent by current backend
  action_items?: ActionItem[];
  people?: MeetingPrepPerson[];
  companies?: MeetingPrepCompany[];
}

type RsvpDisplayEntry = { label: string; dotClass: string };

export const RSVP_DISPLAY: Record<string, RsvpDisplayEntry> = {
  accepted: { label: 'Accepted', dotClass: 'bg-green-500' },
  declined: { label: 'Declined', dotClass: 'bg-red-400' },
  tentative: { label: 'Maybe', dotClass: 'bg-amber-400' },
  needs_action: { label: 'Pending', dotClass: 'bg-neutral-300' },
};

const SECTION_EXPANSION_DEFAULTS: Record<string, Record<string, boolean>> = {
  first_call: {
    summary: true,
    deal_status: false,
    previous_interactions: false,
    people: true,
    company_intel: true,
    meeting_details: false,
  },
  existing_relationship: {
    summary: true,
    deal_status: true,
    previous_interactions: true,
    people: false,
    company_intel: false,
    meeting_details: false,
  },
};

export function getSectionExpanded(mode: string, sectionId: string): boolean {
  return SECTION_EXPANSION_DEFAULTS[mode]?.[sectionId] ?? false;
}

export function deriveSectionsFromFlatMetadata(
  flat: FlatSectionMetadata
): Record<string, { section_metadata?: SectionMetadata }> {
  const sections: Record<string, { section_metadata?: SectionMetadata }> = {};
  const parts: string[] = [];
  if (flat.meeting_count) {
    parts.push(
      `${flat.meeting_count} meeting${flat.meeting_count !== 1 ? 's' : ''}`
    );
  }
  if (flat.email_count) {
    parts.push(`${flat.email_count} email${flat.email_count !== 1 ? 's' : ''}`);
  }
  if (flat.new_attendee_count && flat.new_attendee_count > 0) {
    sections['people'] = {
      section_metadata: {
        badges: [{ label: `${flat.new_attendee_count} new` }],
      },
    };
  }
  if (parts.length > 0) {
    const interactionText = parts.join(' · ');
    sections['summary'] = { section_metadata: { text: interactionText } };
    sections['previous_interactions'] = {
      section_metadata: { text: interactionText },
    };
  }
  return sections;
}

/**
 * Returns deals that are not in a terminal stage (won=probability 1, lost=probability 0).
 */
export function filterActiveDeals(
  deals: CrmObject[],
  getStageByEntityId: (id: string) => Stage | undefined,
  stageTypeNames: Set<string>
): CrmObject[] {
  return deals.filter(deal => {
    const stageEntityId = getStageEntityId(deal, stageTypeNames);
    if (!stageEntityId) {
      return true;
    }
    const stage = getStageByEntityId(stageEntityId);
    if (!stage) {
      return true;
    }
    return stage.probability !== 0 && stage.probability !== 1;
  });
}

export function isValidViewDefinition(
  obj: unknown
): obj is MeetingPrepViewDefinition {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  const candidate = obj as Record<string, unknown>;
  if (typeof candidate['version'] !== 'string') {
    return false;
  }
  if (
    candidate['mode'] !== 'first_call' &&
    candidate['mode'] !== 'existing_relationship'
  ) {
    return false;
  }
  const aiSummary = candidate['ai_summary'];
  if (aiSummary !== null) {
    if (!aiSummary || typeof aiSummary !== 'object') {
      return false;
    }
    const summary = aiSummary as Record<string, unknown>;
    if (typeof summary['preamble'] !== 'string') {
      return false;
    }
    if (!Array.isArray(summary['highlights'])) {
      return false;
    }
    const highlights = summary['highlights'] as unknown[];
    if (
      !highlights.every(
        h =>
          h !== null &&
          typeof h === 'object' &&
          typeof (h as Record<string, unknown>)['type'] === 'string' &&
          typeof (h as Record<string, unknown>)['text'] === 'string'
      )
    ) {
      return false;
    }
    if (
      !Array.isArray(summary['focus_areas']) ||
      !summary['focus_areas'].every((item: unknown) => typeof item === 'string')
    ) {
      return false;
    }
  }
  const { sections } = candidate;
  if (sections !== undefined) {
    if (
      typeof sections !== 'object' ||
      sections === null ||
      Array.isArray(sections)
    ) {
      return false;
    }
  }
  const actionItems = candidate['action_items'];
  if (actionItems !== undefined) {
    if (!Array.isArray(actionItems)) {
      return false;
    }
    if (
      !actionItems.every((item: unknown) => {
        if (item === null || typeof item !== 'object') {
          return false;
        }
        const actionItem = item as Record<string, unknown>;
        return (
          typeof actionItem['text'] === 'string' &&
          (actionItem['source_meeting_title'] === undefined ||
            typeof actionItem['source_meeting_title'] === 'string') &&
          (actionItem['source_meeting_date'] === undefined ||
            typeof actionItem['source_meeting_date'] === 'string')
        );
      })
    ) {
      return false;
    }
  }
  const { people } = candidate;
  if (people !== undefined) {
    if (!Array.isArray(people)) {
      return false;
    }
    if (
      !people.every((p: unknown) => {
        if (p === null || typeof p !== 'object') {
          return false;
        }
        const person = p as Record<string, unknown>;
        return (
          typeof person['email'] === 'string' &&
          typeof person['is_new'] === 'boolean' &&
          (person['entity_id'] === null ||
            typeof person['entity_id'] === 'string') &&
          (person['name'] === null || typeof person['name'] === 'string') &&
          (person['title'] === null || typeof person['title'] === 'string') &&
          (person['company_name'] === null ||
            typeof person['company_name'] === 'string') &&
          (person['research_entity_id'] === null ||
            typeof person['research_entity_id'] === 'string')
        );
      })
    ) {
      return false;
    }
  }
  const { companies } = candidate;
  if (companies !== undefined) {
    if (!Array.isArray(companies)) {
      return false;
    }
    if (
      !companies.every((c: unknown) => {
        if (c === null || typeof c !== 'object') {
          return false;
        }
        const company = c as Record<string, unknown>;
        if (typeof company['entity_id'] !== 'string') {
          return false;
        }
        if (company['name'] !== null && typeof company['name'] !== 'string') {
          return false;
        }
        if (
          company['description'] !== null &&
          typeof company['description'] !== 'string'
        ) {
          return false;
        }
        if (
          company['research_entity_id'] !== null &&
          typeof company['research_entity_id'] !== 'string'
        ) {
          return false;
        }
        const { properties } = company;
        if (
          typeof properties !== 'object' ||
          properties === null ||
          Array.isArray(properties)
        ) {
          return false;
        }
        const props = properties as Record<string, unknown>;
        if (
          !('industry' in props) ||
          !('headcount' in props) ||
          !('location' in props) ||
          !('founded' in props) ||
          !Object.values(props).every(v => v === null || typeof v === 'string')
        ) {
          return false;
        }
        return true;
      })
    ) {
      return false;
    }
  }
  return true;
}
